<script>
import {defineComponent} from 'vue';


const STATE = Object.freeze({
    Idle: 'idle',
    Submitting: 'submitting',
    Success: 'success',
    Error: 'error',
});

// Credentials could be kept for the duration of the page visit
const creds = {
    email: '',
    phone: '',
};

export default defineComponent({
    name: 'affiliateMapContactForm',

    props: {
        affiliate: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            email: creds.email,
            phone: creds.phone,
            message: '',

            STATE,
            state: STATE.Idle,

            responseMsg: '',
        };
    },

    emits: ['cancel'],

    watch: {
        email(val) {
            creds.email = val;
        },
        phone(val) {
            creds.phone = val;
        },
    },

    mounted() {
        this.email = creds.email;
        this.phone = creds.phone;
        this.$nextTick(() => {
            this.$refs.emailInput && this.$refs.emailInput.focus();
        });
    },

    methods: {
        reset() {
            this.state = STATE.Idle;
        },

        isEmailValid() {
            return this.email.match(/@.+\..+$/);
        },

        isPhoneValid() {
            return this.phone.replace(/[^\d]/g, '').length >= 6;
        },

        isMessageValid() {
            return this.message.trim().length > 0;
        },

        isValid() {
            return this.isPhoneValid() && this.isMessageValid();
        },

        openEmailApp() {
            const a = document.createElement('a');
            const subject = encodeURIComponent(`Contact request`);
            const body = encodeURIComponent(`Phone:\n${this.phone}\n\nMessage:\n${this.message}`);
            a.href = `mailto:${this.affiliate.email}?subject=${subject}&body=${body}`;
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },

        async submit() {
            if (this.state === STATE.Submitting) {
                return;
            }
            this.state = STATE.Submitting;
            try {
                const resp = await this.$fetch(`affiliates/${this.affiliate.id}/contact`, {
                    method: 'POST',
                    body: JSON.stringify({
                        email: this.email,
                        phone: this.phone,
                        message: this.message,
                        source_url: location.href,
                    }),
                });
                const data = await resp.json();
                if (data) {
                    if (data.success) {
                        this.responseMsg = data.message;
                        this.state = STATE.Success;
                    } else {
                        throw new Error(data.message);
                    }
                }
            } catch (err) {
                console.warn(err);
                this.state = STATE.Error;
            }

            if (this.state === STATE.Error) {
                this.responseMsg = this.$str('failed_to_send_contact_request');
            }
        },
    },
});
</script>

<template>
    <div class="contactForm">

        <form v-if="state === STATE.Idle" ref="form" @submit.prevent="submit">

            <label class="field for--email" :class="{'is--valid': isEmailValid()}">
                <span>{{ $str('your_email') }}</span>
                <input type="email" v-model="email" ref="emailInput"/>
<!--                <i class="icon fa-regular fa-envelope" aria-hidden="true"></i>-->
            </label>

            <label class="field for--phone" :class="{'is--valid': isPhoneValid()}">
                <span>{{ $str('your_phone') }}</span>
                <input type="text" v-model="phone"/>
<!--                <i class="icon fa-regular fa-phone" aria-hidden="true"></i>-->
            </label>

            <label class="field for--message" :class="{'is--valid': isMessageValid()}">
                <span>{{ $str('your_message') }}</span>
                <textarea v-model="message" rows="5"/>
<!--                <i class="icon fa-regular fa-comment" aria-hidden="true"></i>-->
            </label>

            <button class="submitBtn" :disabled="!isValid()">
                <i class="icon fa-regular fa-paper-plane" aria-hidden="true"></i>
                <span>{{ $str('actions.send_message') }}</span>
            </button>

        </form>

        <div class="busy" v-else-if="state === STATE.Submitting">
            <i class="icon fa-regular fa-sync fa-spin" aria-hidden="true"></i>
        </div>

        <div class="response is--success" v-else-if="state === STATE.Success">
            <div class="responseStatus">
                <i class="icon fa-regular fa-check-circle" aria-hidden="true"></i>
                <h2>{{ $str('thank_you') }}</h2>
            </div>
            <div class="responseContent" v-if="responseMsg">
                {{ responseMsg }}
            </div>
            <button type="button" class="submitBtn" @click="$emit('cancel')">
                <i class="icon fa-regular fa-check" aria-hidden="true"></i>
                <span>{{ $str('actions.ok') }}</span>
            </button>
        </div>

        <div class="response is--error" v-else>
            <div class="responseStatus">
                <i class="icon fa-regular fa-warning" aria-hidden="true"></i>
                <h2>{{ $str('something_went_wrong') }}</h2>
            </div>
            <div class="responseContent" v-if="responseMsg">
                {{ responseMsg }}
            </div>
            <button type="button" class="submitBtn is--alt" @click="reset">
                <i class="icon fa-regular fa-undo" aria-hidden="true"></i>
                <span>{{ $str('actions.try_again') }}</span>
            </button>
            <button type="button" class="submitBtn" @click="openEmailApp">
                <i class="icon fa-regular fa-envelope" aria-hidden="true"></i>
                <span>{{ $str('actions.try_sending_email') }}</span>
            </button>
        </div>

    </div>
</template>

<style lang="scss" scoped>
@import "../defs";

.contactForm {
    position: relative;
    font-size: 1.6rem;

    form {
        margin: 0;
    }

    .field {
        display: block;
        //display: flex;
        //flex-direction: row-reverse;
        //align-items: stretch;
        //justify-content: flex-start;

        & + .field {
            margin-top: 1rem;
        }

        span {
            font-weight: 300;

            &::after {
                content: " *";
                font-weight: 700;
                color: $c_error;
            }
        }

        .icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 3em;
            font-size: 1em;
            border-radius: 5px 0 0 5px;
            color: $c_dark;
            background-color: $c_lighter;
            transition: $transition_fast;
        }
        input,
        textarea {
            display: block;
            height: auto;
            //width: calc(100% - 3rem);
            width: 100%;
            padding: 0.5em 0.5em;
            //margin: 0;
            //font-weight: 700;
            //color: black;
            //border-radius: 0 5px 5px 0;
            //border: 2px solid $c_lighter;
            //border-left: none;
            //outline: none !important;
            //box-shadow: none !important;
            //&::placeholder {
            //    font-weight: 400;
            //    color: $c_gray;
            //}
            //&:focus {
            //    border-color: $c_light;
            //    & + .fa {
            //        background-color: $c_light;
            //    }
            //}
            &::placeholder {
                font-weight: 400;
                color: $c_gray;
            }
        }
        &.is--valid {
            input, textarea {
                border-color: $c_success;
            }
            .icon {
                color: $c_success;
            }
        }
        &:not(.is--valid) {
            .icon {
                color: $c_error;
            }
        }
    }

    .submitBtn {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 2rem 0 0 auto;
        padding: 0.5em 2em;
        font-size: 1.2em;
        color: white;
        background-color: $c_dark;
        .icon {
            margin-right: 0.5em;
        }
        &:hover, &:focus {
            background-color: black;
        }
        &:disabled {
            color: $c_lighter;
            background-color: $c_gray;
            cursor: initial;
        }
        &.is--alt {
            border: 2px solid $c_dark;
            color: $c_dark;
            background-color: transparent;
            &:hover, &:focus {
                border-color: $c_darker;
                background-color: transparent;
            }
            &:disabled {
                border-color: $c_gray;
                color: $c_gray_darker;
                background-color: transparent;
            }
        }
    }

    .busy {
        text-align: center;
        .icon {
            margin: 2rem 0;
            font-size: 5rem;
            color: $c_gray;
        }
    }
    .response {
        text-align: center;
        .responseStatus {
            .icon {
                font-size: 5rem;
            }
        }

        .responseContent {

        }
        &.is--error {
            .responseStatus {
                .icon {
                    color: $c_error;
                }
            }
        }
        &.is--success {
            .responseStatus {
                .icon {
                    color: $c_success;
                }
            }
        }
    }
}

</style>
